import React, { FC } from 'react'

import { alpha, useTheme } from '@mui/material'
import TableRow from '@mui/material/TableRow'

import { Trade } from '../../pages/TradingLog/types'
import { useThemeMode } from '../../theme/ThemeModeContext'

import { SharingFeatureCell } from './SharingFeatureCell'
import { TableBodyCell } from './TableBodyCell'
import TradeAiCell from './TradeAiCell'
import { TradingLogBodyCellSelect } from './TradingLogBodyCellSelect'
import { AvailableTableTypes, TableBodyRowProps } from './types'

export const TableBodyRow: FC<TableBodyRowProps<AvailableTableTypes>> = ({
  logEntry,
  columnOrder,
  onRowClick,
  selectedRow,
  displaySelectTradingLogRow = false,
  onAiAnalysis,
  showTradeInfoColumn = true,
  showAiAnalysisColumn = true,
  analysisLoading = false,
}) => {
  const { palette } = useTheme()
  const { resolvedMode } = useThemeMode()

  const hoverColor =
    resolvedMode === 'dark'
      ? alpha(palette.background.paper, 0.95)
      : alpha(palette.blue[500] as unknown as string, 0.08)

  const tradeEntry = 'priceCurrency' in logEntry ? (logEntry as Trade) : null
  const currency = tradeEntry?.priceCurrency || ''
  const rowId = 'id' in logEntry ? String(logEntry.id) : undefined
  const error =
    'error' in logEntry && (typeof logEntry.error === 'string' || logEntry.error === null)
      ? logEntry.error
      : undefined
  const canRenderAiCell = Boolean(showAiAnalysisColumn && rowId && onAiAnalysis)
  const canRenderShareCell = Boolean(
    showTradeInfoColumn && rowId && 'ticket' in logEntry && 'closeTime' in logEntry,
  )

  return (
    <TableRow
      sx={{
        '& .share.hidden': {
          display: 'none',
        },
        bgcolor: palette.background.paper,
        '&:hover': {
          bgcolor: hoverColor,
          cursor: 'pointer',
        },
      }}
    >
      {displaySelectTradingLogRow && 'id' in logEntry && (
        <TradingLogBodyCellSelect id={Number(logEntry.id)} />
      )}

      {canRenderAiCell && (
        <TradeAiCell
          onClick={onAiAnalysis as () => void}
          analyzed={Boolean('tradeAiAnalysis' in logEntry ? logEntry.tradeAiAnalysis : null)}
          loading={analysisLoading}
        />
      )}

      {canRenderShareCell && 'id' in logEntry && (
        <SharingFeatureCell id={Number(logEntry.id)} logEntry={logEntry as Trade} />
      )}

      {columnOrder.map((columnKey) => {
        return (
          <TableBodyCell
            key={columnKey}
            type={columnKey}
            value={logEntry[columnKey as keyof AvailableTableTypes]}
            selectedRow={selectedRow}
            currency={currency}
            id={rowId}
            error={error}
            onClick={onRowClick}
          />
        )
      })}
    </TableRow>
  )
}
